import { formatClubDate, formatClubTime } from './clubTime';
/**
 * Competition Day Registration Utility Functions
 * 
 * Provides validation and business logic for competition day check-in operations.
 */

export type RegistrationType = 'INDIVIDUAL_MEMBER' | 'FAMILY_CHECKIN' | 'NON_MEMBER';

export interface CheckInWindowResult {
  isOpen: boolean;
  message: string;
}

/**
 * Validates email format using standard email regex pattern.
 * 
 * @param email - Email address to validate
 * @returns true if email format is valid, false otherwise
 */
export function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') {
    return false;
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Calculates the registration fee based on registration type.
 * Members and family members: $10
 * Non-members: $15
 * 
 * @param registrationType - The type of registration
 * @returns Fee amount in dollars
 */
export function calculateFee(registrationType: RegistrationType): number {
  switch (registrationType) {
    case 'INDIVIDUAL_MEMBER':
    case 'FAMILY_CHECKIN':
      return 10;
    case 'NON_MEMBER':
      return 15;
    default:
      throw new Error(`Invalid registration type: ${registrationType}`);
  }
}

/**
 * Validates phone number is not empty.
 * 
 * @param phone - Phone number to validate
 * @returns true if phone number is provided, false otherwise
 */
export function validatePhoneNumber(phone: string): boolean {
  return phone !== null && phone !== undefined && phone.trim() !== '';
}

/**
 * Validates that a required field is not empty.
 * 
 * @param value - Field value to validate
 * @param fieldName - Name of the field (for error messages)
 * @returns Object with isValid flag and error message if invalid
 */
export function validateRequiredField(value: string, fieldName: string): { isValid: boolean; error?: string } {
  if (!value || value.trim() === '') {
    return {
      isValid: false,
      error: `${fieldName} is required`
    };
  }
  
  return { isValid: true };
}

/**
 * Formats a date for display in competition details.
 * 
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatCompetitionDate(date: Date): string {
  return formatClubDate(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats a time for display.
 * 
 * @param date - Date object containing the time
 * @returns Formatted time string (e.g., "7:00 AM")
 */
export function formatTime(date: Date): string {
  return formatClubTime(date, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
